import { useMemo, useState, type ChangeEventHandler, useCallback } from 'react'
import { toast } from 'react-toastify'

import { Textarea } from '@loadsmart/loadsmart-ui'
import { Dialog, Field, Layout, Text } from '@loadsmart/miranda-react'
import { toCSSValue } from '@loadsmart/miranda-react/dist/tokens'

import { RequestStatus } from 'core/domain/Request'
import { useAbortController } from 'core/ui/hooks/useAbortController'

import { type Shipment } from '../domain/Shipment'
import { type ShipmentCharge, isChargeStatusOutdated } from '../domain/ShipmentCharges'
import { formatChargeShipperAction } from '../presenters/shipmentChargePresenter'
import { useCancelCharge } from './hooks/useShipmentChargesActions'

const CHARGE_REASON_MAX_CHARS = 150

type DialogContentProps = {
  onClose: () => void
  onConfirm: () => void
  onCancelReasonChange: ChangeEventHandler<HTMLTextAreaElement>
  isLoading: boolean
  chargeUpdated?: ShipmentCharge
  cancelReason?: string
  shipperEmail?: string
}

function DialogContent({
  onClose,
  onConfirm,
  onCancelReasonChange,
  chargeUpdated,
  isLoading,
  cancelReason,
  shipperEmail,
}: DialogContentProps) {
  if (chargeUpdated && isChargeStatusOutdated(chargeUpdated)) {
    return (
      <>
        <Dialog.Body>
          <Layout.Stack>
            <Text>
              <span style={{ backgroundColor: toCSSValue('color-warning-20') }}>
                Shipper already {formatChargeShipperAction(chargeUpdated.status.value)} this charge
              </span>
            </Text>
            <Text>
              If you need to change any information, please contact them directly by email [
              <a
                href={`mailto:${shipperEmail}`}
                style={{ color: toCSSValue('color-text-link'), textDecoration: 'underline' }}
              >
                {shipperEmail}
              </a>
              ]
            </Text>
          </Layout.Stack>
        </Dialog.Body>
        <Dialog.Actions>
          <Dialog.ActionTertiary onClick={onClose} disabled={isLoading}>
            Close
          </Dialog.ActionTertiary>
        </Dialog.Actions>
      </>
    )
  }
  return (
    <>
      <Dialog.Body>
        <Field required>
          <Field.Label>Reason</Field.Label>
          <Textarea
            aria-label="Reason"
            placeholder="Tell the shipper why you are cancelling the charge"
            value={cancelReason}
            onChange={onCancelReasonChange}
            maxLength={CHARGE_REASON_MAX_CHARS}
            disabled={isLoading}
          />
          <Field.Hint>Max {CHARGE_REASON_MAX_CHARS} chars</Field.Hint>
        </Field>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionTertiary onClick={onClose} disabled={isLoading}>
          Do not cancel
        </Dialog.ActionTertiary>
        <Dialog.ActionPrimary
          onClick={onConfirm}
          disabled={!cancelReason || isLoading}
          loading={isLoading}
        >
          Confirm cancel
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </>
  )
}

type CancelChargeDialogProps = {
  isOpen: boolean
  onClose: () => void
  onClosed: () => void
  shipment: Shipment
  chargeUUID: string
  onUpdateCharge: (updatedCharge?: ShipmentCharge) => void
}

export function CancelChargeDialog({
  isOpen,
  onClose,
  onClosed,
  shipment,
  chargeUUID,
  onUpdateCharge,
}: CancelChargeDialogProps) {
  const onCancelChargeSuccess = useCallback(
    (charge?: ShipmentCharge) => {
      onUpdateCharge(charge)
      toast.success('Charge canceled successfully.')
      onClose()
    },
    [onClose, onUpdateCharge]
  )

  const onCancelChargesError = useCallback(
    (error: string, charge?: ShipmentCharge) => {
      if (isChargeStatusOutdated(charge)) {
        onUpdateCharge(charge)
      } else {
        toast.error(error)
        onClose()
      }
    },
    [onClose, onUpdateCharge]
  )

  const onCancelChargesUnknownError = useCallback((error: string) => {
    toast.error(error)
  }, [])

  const { chargeUpdated, cancelChargeStatus, cancelChargePatch } = useCancelCharge({
    onSuccess: onCancelChargeSuccess,
    onError: onCancelChargesError,
    onUnknownError: onCancelChargesUnknownError,
  })
  const abortController = useAbortController()
  const [cancelReason, setCancelReason] = useState<string>()

  const isLoading = useMemo(
    () => cancelChargeStatus === RequestStatus.Pending,
    [cancelChargeStatus]
  )

  function onConfirm() {
    if (cancelReason) {
      cancelChargePatch(shipment.uuid, chargeUUID, cancelReason, abortController)
    }
  }

  return (
    <Dialog size="small" variant="danger" open={isOpen} onClose={onClose} onClosed={onClosed}>
      <Dialog.Header>
        Confirm cancel
        <Dialog.Close onClick={onClose} />
      </Dialog.Header>
      <DialogContent
        onClose={onClose}
        onConfirm={onConfirm}
        onCancelReasonChange={event => {
          setCancelReason(event.target.value)
        }}
        isLoading={isLoading}
        chargeUpdated={chargeUpdated}
        cancelReason={cancelReason}
        shipperEmail={shipment.shipper?.email}
      />
    </Dialog>
  )
}
